import React, { useState } from 'react';
import { Link } from 'expo-router';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useHydratedWindowDimensions } from '../../hooks/useHydratedWindowDimensions';
import colors from '../../lib/colors';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { Heading, Section, SectionLabel } from '../about/AboutPrimitives';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { resolveMessage } from '../../lib/i18n/messages';
import { HOME_FAQ_ITEMS } from './homeFaqContent';

export default function HomeFAQ({ fontsLoaded }: { fontsLoaded: boolean }) {
    const { i18n } = useLingui();
    const { width } = useHydratedWindowDimensions();
    const isNarrow = width < 760;
    const [expanded, setExpanded] = useState<Set<number>>(new Set([0]));

    const toggleItem = (index: number) => {
        setExpanded((current) => {
            const next = new Set(current);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    return (
        <Section backgroundColor={colors.bgWeak} top={80} bottom={80} isNarrow={isNarrow}>
            <SectionLabel fontsLoaded={fontsLoaded}><Trans id="home.homefaq.faq" comment="UI copy in components/home/HomeFAQ.tsx">FAQ</Trans></SectionLabel>
            <Heading fontsLoaded={fontsLoaded}><Trans id="home.homefaq.frequentlyAskedQuestions" comment="UI copy in components/home/HomeFAQ.tsx">Frequently asked questions</Trans></Heading>
            <View style={styles.faqList}>
                {HOME_FAQ_ITEMS.map((item, index) => {
                    const isExpanded = expanded.has(index);
                    const question = resolveMessage(i18n, item.question);
                    const answer = resolveMessage(i18n, item.answer);
                    const link = 'link' in item ? item.link : undefined;
                    return (
                        <View key={item.question.id} style={styles.faqItem}>
                            <Pressable
                                accessibilityRole="button"
                                onPress={() => toggleItem(index)}
                                style={styles.faqQuestion}
                            >
                                <Text style={[styles.faqIcon, fontsLoaded && interRegular]}>
                                    {isExpanded ? '-' : '+'}
                                </Text>
                                <Text style={[styles.faqQuestionText, fontsLoaded && interSemiBold]}>
                                    {question}
                                </Text>
                            </Pressable>
                            <View style={[styles.faqAnswerWrap, !isExpanded && styles.faqAnswerCollapsed]}>
                                <Text style={[styles.faqAnswer, fontsLoaded && interRegular]}>
                                    {answer}
                                </Text>
                                {link ? (
                                    <Link
                                        href={i18n.locale === 'es' ? link.spanishHref : link.href}
                                        style={[styles.faqAnswerLink, fontsLoaded && interSemiBold]}
                                    >
                                        {resolveMessage(i18n, link.label)}
                                    </Link>
                                ) : null}
                            </View>
                        </View>
                    );
                })}
            </View>
        </Section>
    );
}

const styles = StyleSheet.create({
    faqList: {
        gap: 12,
        marginTop: 32,
    },
    faqItem: {
        backgroundColor: '#ffffff',
        borderColor: colors.border,
        borderRadius: 14,
        borderWidth: 1.5,
        overflow: 'hidden',
    },
    faqQuestion: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 14,
        paddingHorizontal: 20,
        paddingVertical: 18,
    },
    faqIcon: {
        color: colors.primaryGreen,
        fontSize: 24,
        lineHeight: 26,
        minWidth: 22,
        textAlign: 'center',
    },
    faqQuestionText: {
        color: colors.text900,
        flex: 1,
        fontSize: 16,
        fontWeight: '700',
        lineHeight: 23,
    },
    faqAnswerWrap: {
        maxHeight: 420,
        overflow: 'hidden',
        paddingBottom: 20,
        paddingHorizontal: 56,
        paddingTop: 0,
    },
    faqAnswerCollapsed: {
        maxHeight: 0,
        paddingBottom: 0,
    },
    faqAnswer: {
        color: colors.text600,
        fontSize: 15,
        lineHeight: 24,
    },
    faqAnswerLink: {
        color: colors.primaryGreenDark,
        fontSize: 15,
        lineHeight: 24,
        marginTop: 8,
        textDecorationLine: 'underline',
    },
});
